<template>
  <div class="knowledge-course-recommendations">
    <div class="recommendations-header">
      <h3>推荐课程</h3>
      <p class="subtitle">基于您的学习偏好为您推荐</p>
    </div>

    <div v-if="loading" class="loading-container">
      <el-skeleton :rows="2" animated />
    </div>

    <div v-else-if="recommendations.length === 0" class="empty-state">
      <p>暂无推荐课程</p>
    </div>

    <div v-else class="recommendations-grid">
      <div 
        v-for="course in recommendations" 
        :key="course.kcId" 
        class="recommendation-card"
        @click="viewCourse(course)"
      >
        <div class="card-header">
          <div class="course-badges">
            <el-tag :type="getLevelTagType(course.level)" size="small">
              {{ getLevelText(course.level) }}
            </el-tag>
            <el-tag type="info" size="small">{{ course.category }}</el-tag>
          </div>
          <div class="recommendation-score">
            <span class="score-text">推荐度</span>
            <span class="score-value">{{ course.recommendationScore }}%</span>
          </div>
        </div>

        <div class="card-content">
          <h4 class="course-title">{{ course.name }}</h4>
          <p class="course-description">{{ course.description || '暂无描述' }}</p>
          
          <div class="course-meta">
            <div class="meta-item">
              <el-icon><Clock /></el-icon>
              <span>{{ getCourseDuration(course) }} 分钟</span>
            </div>
            <div class="meta-item">
              <el-icon><Document /></el-icon>
              <span>{{ getKnowledgePointCount(course) }} 个知识点</span>
            </div>
          </div>

          <div class="recommendation-reason">
            <span class="reason-label">推荐理由:</span>
            <span class="reason-text">{{ course.recommendationReason }}</span>
          </div>
        </div>

        <div class="card-footer">
          <el-button type="primary" size="small" @click.stop="viewCourse(course)">
            {{ courseActionLabel(course) }}
          </el-button>
          <el-button type="info" size="small" @click.stop="showCoursePreview(course)">
            大纲
          </el-button>
        </div>
      </div>
    </div>

    <!-- 课程大纲对话框 -->
    <el-dialog
      v-model="previewDialogVisible"
      title="课程大纲"
      width="60%"
      :before-close="handlePreviewClose"
    >
      <div v-if="previewCourse" class="course-preview">
        <div v-if="previewCourse.banner" class="preview-cover">
          <img class="preview-banner" :src="previewCourse.banner" alt="课程封面" />
        </div>
        <div class="preview-badges">
          <el-tag :type="getLevelTagType(previewCourse.level)" size="small">
            {{ getLevelText(previewCourse.level) }}
          </el-tag>
          <el-tag type="info" size="small">{{ previewCourse.category }}</el-tag>
          <el-tag type="success" size="small">推荐度: {{ previewCourse.recommendationScore }}%</el-tag>
        </div>

        <div class="preview-header">
          <h3>{{ previewCourse.name }}</h3>
        </div>
        
        <div class="preview-description">
          <h4>课程描述</h4>
          <p>{{ previewCourse.description || '暂无描述' }}</p>
        </div>
        
        <div class="preview-reason">
          <h4>推荐理由</h4>
          <p>{{ previewCourse.recommendationReason }}</p>
        </div>
        
        <div class="preview-structure">
          <h4>课程大纲</h4>
          <div v-if="previewCourse.kpTree && previewCourse.kpTree.children" class="structure-tree">
            <div v-for="(node, index) in previewCourse.kpTree.children" :key="index" class="structure-node">
              <div class="node-header">
                <span class="node-icon">📖</span>
                <span class="node-name">{{ node.name || '未命名知识点' }}</span>
                <span class="node-seq">{{ node.seqname }}</span>
              </div>
              <div v-if="node.children && node.children.length" class="node-children">
                <div v-for="(child, childIndex) in node.children" :key="childIndex" class="child-node">
                  <span class="child-icon">📝</span>
                  <span class="child-name">{{ child.name || '未命名知识点' }}</span>
                  <span class="child-seq">{{ child.seqname }}</span>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="no-structure">
            <p>暂无课程结构信息</p>
          </div>
        </div>
      </div>
      
      <template #footer>
        <el-button @click="previewDialogVisible = false">关闭</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { Clock, Document } from '@element-plus/icons-vue'
import { isFreeCourse, coursePrimaryActionLabel } from '@/utils/courseAccess'
import { isLoggedIn, promptLogin, loginPageRoute } from '@/utils/loginFlow'

export default {
  name: 'KnowledgeCourseRecommendations',
  components: {
    Clock,
    Document
  },
  data() {
    return {
      loading: true,
      recommendations: [],
      previewDialogVisible: false,
      previewCourse: null
    }
  },
  created() {
    this.loadRecommendations()
  },
  methods: {
    async loadRecommendations() {
      try {
        this.loading = true
        const token = localStorage.getItem('jwt_token')
        const response = await fetch('/api/v1/knowledge/courses/list', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            page: 1,
            pageSize: 6, // 只获取6个推荐课程
            keyword: ''
          })
        })
        
        const data = await response.json()
        if (data.code === 200) {
          // 为课程添加推荐分数和推荐理由
          this.recommendations = (data.data.list || []).map(course => ({
            ...course,
            recommendationScore: this.generateRecommendationScore(course),
            recommendationReason: this.generateRecommendationReason(course)
          }))
        }
      } catch (error) {
        console.error('Failed to load recommendations:', error)
        this.$message.error('获取推荐课程失败')
      } finally {
        this.loading = false
      }
    },
    
    generateRecommendationScore(course) {
      // 基于课程等级、分类等因素生成推荐分数
      let score = 70 // 基础分数
      
      // 根据等级调整分数
      if (course.level === 1) score += 10 // 初级课程更容易上手
      else if (course.level === 2) score += 5 // 中级课程适中
      
      // 根据分类调整分数
      const popularCategories = ['心理学', '逻辑学', '数学', '哲学']
      if (popularCategories.includes(course.category)) {
        score += 15
      }
      
      // 添加随机因素
      score += Math.floor(Math.random() * 20)
      
      return Math.min(100, Math.max(60, score))
    },
    
    generateRecommendationReason(course) {
      const reasons = [
        `与您之前学习的${course.category}类课程相关`,
        `适合您的${this.getLevelText(course.level)}水平`,
        '热门课程，学习人数众多',
        '内容实用，应用价值高',
        '结构清晰，易于理解',
        '包含丰富的实例和练习'
      ]
      
      return reasons[Math.floor(Math.random() * reasons.length)]
    },
    
    getLevelText(level) {
      const levels = {
        1: '初级',
        2: '中级',
        3: '高级'
      }
      return levels[level] || '未知'
    },
    
    getLevelTagType(level) {
      const types = {
        1: 'success',
        2: 'warning',
        3: 'danger'
      }
      return types[level] || 'info'
    },
    
    getCourseDuration(course) {
      if (!course.kpTree) return '未知'
      try {
        const tree = JSON.parse(course.kpTree)
        const nodeCount = this.countNodes(tree)
        return Math.max(30, nodeCount * 15) // 每个知识点约15分钟
      } catch {
        return '未知'
      }
    },
    
    getKnowledgePointCount(course) {
      if (!course.kpTree) return 0
      try {
        const tree = JSON.parse(course.kpTree)
        return this.countNodes(tree)
      } catch {
        return 0
      }
    },
    
    countNodes(tree) {
      if (!tree.children) return 0
      let count = tree.children.length
      tree.children.forEach(child => {
        if (child.children) {
          count += this.countNodes(child)
        }
      })
      return count
    },
    
    courseActionLabel(course) {
      return coursePrimaryActionLabel(course, isLoggedIn())
    },

    async viewCourse(course) {
      if (isFreeCourse(course)) {
        this.$router.push(`/knowledge/courses/display/${course.kcId}`)
        return
      }
      if (!isLoggedIn()) {
        const result = await promptLogin('需要登录后才能购买并学习该课程，是否前往登录？')
        if (result === 'navigate') {
          this.$router.push(loginPageRoute(`/knowledge/courses/display/${course.kcId}`))
        }
        return
      }
      this.$router.push(`/knowledge/courses/display/${course.kcId}`)
    },
    
    showCoursePreview(course) {
      this.previewCourse = {
        ...course,
        kpTree: course.kpTree ? JSON.parse(course.kpTree) : { children: [] }
      }
      this.previewDialogVisible = true
    },
    
    handlePreviewClose() {
      this.previewDialogVisible = false
      this.previewCourse = null
    },
    
    startLearning() {
      if (this.previewCourse) {
        this.viewCourse(this.previewCourse)
        this.previewDialogVisible = false
      }
    }
  }
}
</script>

<style scoped>
.knowledge-course-recommendations {
  padding: 20px;
  background: #f5f7fa;
  border-radius: 8px;
}

.recommendations-header {
  text-align: center;
  margin-bottom: 30px;
}

.recommendations-header h3 {
  margin: 0 0 8px 0;
  color: #2c3e50;
  font-size: 24px;
}

.subtitle {
  margin: 0;
  color: #7f8c8d;
  font-size: 14px;
}

.loading-container {
  padding: 20px;
}

.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #7f8c8d;
}

.recommendations-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
}

.recommendation-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  cursor: pointer;
  border-left: 4px solid #409EFF;
}

.recommendation-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 15px;
}

.course-badges {
  display: flex;
  gap: 8px;
}

.recommendation-score {
  text-align: right;
}

.score-text {
  display: block;
  font-size: 12px;
  color: #7f8c8d;
  margin-bottom: 2px;
}

.score-value {
  font-size: 18px;
  font-weight: bold;
  color: #409EFF;
}

.card-content {
  margin-bottom: 15px;
}

.course-title {
  margin: 0 0 8px 0;
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
  line-height: 1.4;
}

.course-description {
  color: #7f8c8d;
  font-size: 14px;
  line-height: 1.5;
  margin: 0 0 12px 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.course-meta {
  display: flex;
  gap: 16px;
  margin-bottom: 12px;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 4px;
  color: #95a5a6;
  font-size: 12px;
}

.recommendation-reason {
  background: #f8f9fa;
  padding: 8px 12px;
  border-radius: 6px;
  border-left: 3px solid #409EFF;
}

.reason-label {
  font-size: 12px;
  color: #409EFF;
  font-weight: 500;
  margin-right: 8px;
}

.reason-text {
  font-size: 12px;
  color: #6c757d;
}

.card-footer {
  display: flex;
  gap: 8px;
}

/* 大纲对话框样式 */
.course-preview {
  max-height: 60vh;
  overflow-y: auto;
}

.preview-cover {
  width: 100%;
  height: 180px;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 0;
}

.preview-banner {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.preview-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 12px 0 0;
}

.preview-header {
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #eee;
}

.preview-header h3 {
  margin: 0 0 10px 0;
  color: #2c3e50;
  font-size: 20px;
}

.preview-meta {
  display: flex;
  gap: 8px;
}

.preview-description,
.preview-reason {
  margin-bottom: 20px;
}

.preview-description h4,
.preview-reason h4 {
  margin: 0 0 8px 0;
  color: #2c3e50;
  font-size: 16px;
}

.preview-description p,
.preview-reason p {
  margin: 0;
  color: #7f8c8d;
  line-height: 1.6;
}

.preview-structure h4 {
  margin: 0 0 15px 0;
  color: #2c3e50;
  font-size: 16px;
}

.structure-tree {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.structure-node {
  background: #f8f9fa;
  border-radius: 6px;
  padding: 12px;
}

.node-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.node-icon {
  font-size: 16px;
}

.node-name {
  flex: 1;
  font-weight: 500;
  color: #2c3e50;
}

.node-seq {
  background: #e9ecef;
  color: #6c757d;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
}

.node-children {
  margin-left: 20px;
  padding-left: 10px;
  border-left: 2px solid #dee2e6;
}

.child-node {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
}

.child-icon {
  font-size: 14px;
}

.child-name {
  flex: 1;
  color: #6c757d;
  font-size: 14px;
}

.child-seq {
  background: #e9ecef;
  color: #6c757d;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
}

.no-structure {
  text-align: center;
  padding: 20px;
  color: #6c757d;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .recommendations-grid {
    grid-template-columns: 1fr;
  }
  
  .card-header {
    flex-direction: column;
    gap: 10px;
  }
  
  .recommendation-score {
    align-self: flex-end;
  }
  
  .card-footer {
    flex-direction: column;
  }
  
  .course-meta {
    flex-direction: column;
    gap: 8px;
  }
}
</style> 